import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { CustomerSearchResult } from '@/lib/customers/types';
// Core-100 R4 wave 3 (CORE-AY D): the one customer-search request + reply reading (shared with the TopBar search).
import { CUSTOMER_SEARCH_MIN_CHARS, runCustomerSearch } from '@/lib/search/customerSearchContract';

interface UseCustomerSearchOptions {
  debounceMs?: number;
  limit?: number;
}

interface UseCustomerSearchResult {
  results: CustomerSearchResult[];
  isSearching: boolean;
  error: Error | null;
  search: (query: string) => void;
  clearResults: () => void;
  /** The query whose answer `results` / `error` hold (null before any answer) — so "nothing matched" can be said. */
  searchedQuery: string | null;
}


/**
 * Hook for global customer/vehicle search.
 * Uses the customer-search edge function with blind-index lookup.
 */
export function useCustomerSearch(options: UseCustomerSearchOptions = {}): UseCustomerSearchResult {
  const { debounceMs = 300, limit = 10 } = options;
  const { activeShop } = useAuth();
  
  const [results, setResults] = useState<CustomerSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
    setSearchedQuery(null);
  }, []);

  const search = useCallback((query: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (!query || query.trim().length < CUSTOMER_SEARCH_MIN_CHARS) {
      clearResults();
      return;
    }
    
    timeoutRef.current = setTimeout(async () => {
      if (!activeShop?.id) {
        setError(new Error('No active shop selected'));
        return;
      }
      
      setIsSearching(true);
      setError(null);
      
      const currentRequestId = ++requestIdRef.current;
      const term = query.trim();
      // A failed search (transport error, an error in the reply, no results array) is an ERROR, never an empty list.
      const outcome = await runCustomerSearch(supabase, activeShop.id, term, limit);
      if (currentRequestId !== requestIdRef.current) return; // a newer search owns the state
      if (outcome.ok) {
        setResults(outcome.results);
        setError(null);
      } else {
        setResults([]);
        setError(new Error(outcome.reason ?? 'Customer search failed'));
      }
      setSearchedQuery(term);
      setIsSearching(false);
    }, debounceMs);
  }, [activeShop?.id, debounceMs, limit, clearResults]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    results,
    isSearching,
    error,
    search,
    clearResults,
    searchedQuery,
  };
}
